import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import loadingLogo from '../assets/brand/vo2-loading-logo.png';
import './NmLoadingOverlay.css';

export interface NmLoaderProps {
  size?: 'xs' | 'sm' | 'md' | 'lg' | number;
  /** When supplied, the accessible status is also visible. */
  label?: string;
  inline?: boolean;
  /** Busy controls already provide their own accessible name and state. */
  decorative?: boolean;
  className?: string;
}

/** One brand asset and motion for pages, panels, and busy controls. */
export function NmLoader({ size = 'md', label, inline = false, decorative = false, className = '' }: NmLoaderProps) {
  // The mark is also used before translations have initialized at app startup.
  return decorative
    ? <LoaderMark size={size} label={label} inline={inline} decorative className={className} />
    : <LocalizedLoader size={size} label={label} inline={inline} className={className} />;
}

function LocalizedLoader(props: NmLoaderProps) {
  const { t } = useTranslation();
  return <LoaderMark {...props} statusLabel={props.label ?? t('common.loading')} />;
}

function LoaderMark({ size = 'md', label, inline, decorative, className = '', statusLabel }: NmLoaderProps & { statusLabel?: string }) {
  const style = {
    '--nm-loader-asset': `url("${loadingLogo}")`,
    ...(typeof size === 'number' ? { '--nm-loader-size': `${size}px` } : {}),
  } as CSSProperties;
  return (
    <span className={`nm-loader nm-loader--${typeof size === 'number' ? 'custom' : size}${inline ? ' nm-loader--inline' : ''} ${className}`.trim()}
      style={style} role={decorative ? undefined : 'status'} aria-live={decorative ? undefined : 'polite'}
      aria-label={statusLabel} aria-hidden={decorative || undefined}>
      <span className="nm-loader__logo" aria-hidden="true" dir="ltr">
        <span className="nm-loader__blade nm-loader__blade--leading"><img src={loadingLogo} alt="" draggable={false} /></span>
        <span className="nm-loader__blade nm-loader__blade--following"><img src={loadingLogo} alt="" draggable={false} /></span>
      </span>
      {label && <span className="nm-loader__label">{label}</span>}
    </span>
  );
}
